import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Package, Clock, MapPin, ChevronLeft, Settings2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import type { SolicitacaoStatus } from "@/services/schemas/solicitacao.schema";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/base";
import { useSolicitacoes } from "@/hooks/useSolicitacoes";
import { useState } from "react";
import { STATUS_LABELS, STATUS_COLORS } from "@/lib/status-mapping";

export const Route = createFileRoute("/app/entregas/")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (!token || !user) {
      throw redirect({
        to: "/auth/identify",
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);


  const {
    solicitacoes,
    isLoading,
    error,
    activeTab,
    statusFilter,
    hasActiveFilters,
    hasAnyDelivery,
    handleTabChange,
    handleStatusFilterChange,
    handleClearFilters,
  } = useSolicitacoes();
  console.log({ solicitacoes })

  return (
    <div className={`min-h-screen ${solicitacoes.length === 0 && !isLoading ? 'bg-slate-50' : ''}`}>
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="container mx-auto px-4 py-3 max-w-2xl">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: "/app" })}
              className="rounded-full hover:bg-black/5 -ml-2"
            >
              <ChevronLeft className="h-5 w-5 text-primary" />
            </Button>

            <h1 className="text-base font-semibold text-slate-900">Entregas</h1>

            <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full hover:bg-black/5 -mr-2 relative"
                >
                  <Settings2 className="h-5 w-5 text-primary" />
                  {hasActiveFilters && (
                    <span className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-medium">
                      1
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="h-auto max-h-[85vh] rounded-t-3xl border-t border-black/5 pb-8 overflow-y-auto"
              >
                <SheetHeader className="pb-4 border-b">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <SheetTitle className="text-xl">
                        Filtros de status
                      </SheetTitle>
                      <p className="text-sm text-gray-600">
                        Refine a lista de entregas por status
                      </p>
                    </div>
                  </div>
                </SheetHeader>
                <div className="space-y-6 pt-4 px-4">
                  <div className="flex flex-col gap-3">
                    {Object.entries(STATUS_LABELS).map(([status, label]) => {
                      // Only show statuses relevant to the active tab, unless "all"
                      if (activeTab === "active" && !["analysis", "searching", "delivering"].includes(status)) return null;
                      if (activeTab === "history" && !["delivered", "canceled", "rejected"].includes(status)) return null;

                      const isSelected = statusFilter === status;
                      const colors = STATUS_COLORS[status as SolicitacaoStatus];

                      return (
                        <button
                          key={status}
                          onClick={() => {
                            handleStatusFilterChange(
                              isSelected ? null : (status as SolicitacaoStatus)
                            );
                            // setIsFilterSheetOpen(false); // Optional: close on select
                          }}
                          className={`flex items-center justify-between w-full px-4 py-3 rounded-2xl border transition-all ${
                            isSelected
                              ? `${colors.bg} border-transparent`
                              : "bg-white border-black/5 hover:bg-black/5"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`h-2.5 w-2.5 rounded-full ${colors.dot}`}
                            />
                            <span
                              className={`text-sm font-semibold ${
                                isSelected ? colors.text : "text-slate-700"
                              }`}
                            >
                              {label}
                            </span>
                          </div>
                          {isSelected && (
                            <div className={`h-4 w-4 rounded-full ${colors.dot} bg-opacity-20 flex items-center justify-center`}>
                               <div className={`h-1.5 w-1.5 rounded-full bg-white`} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex gap-3 pt-4">
                     <Button
                        variant="outline"
                        onClick={handleClearFilters}
                        disabled={!hasActiveFilters}
                        className="flex-1 h-10 rounded-xl "
                      >
                        Limpar
                      </Button>
                      <Button
                        onClick={() => setIsFilterSheetOpen(false)}
                        className="flex-1 h-10 rounded-xl"
                      >
                        Aplicar
                      </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <div className="sticky top-[57px] z-40 max-w-2xl mx-auto">
        <div className="container mx-auto px-4 pt-3 pb-2">
          <Tabs
            value={activeTab}
            onValueChange={(v) => handleTabChange(v as typeof activeTab)}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3 bg-white/80 backdrop-blur-xl rounded-3xl p-1 border border-black/5">
              <TabsTrigger
                value="active"
                className="data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
              >
                Ativas
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
              >
                Histórico
              </TabsTrigger>
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
              >
                Todas
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <main className={`container mx-auto max-w-2xl px-4 py-3 pb-24 ${solicitacoes.length === 0 && !isLoading ? '' : ''}`}>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-3 animate-pulse border border-black/5"
              >
                <div className="flex gap-3">
                  <div className="h-9 w-9 bg-slate-200 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                    <div className="h-2.5 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="bg-rose-50 text-rose-700 rounded-2xl p-5 inline-block border border-rose-100">
              <p className="font-medium text-sm">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
                className="mt-3 rounded-xl border-rose-200 hover:bg-rose-100"
              >
                Tentar Novamente
              </Button>
            </div>
          </div>
        ) : solicitacoes.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
            {hasActiveFilters ? (
              <>
                <div className="bg-slate-100 rounded-full p-4 mb-4">
                  <Package className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  Nenhuma entrega encontrada
                </h3>
                <p className="text-sm text-slate-500 max-w-xs">
                  Tente ajustar os filtros para encontrar entregas
                </p>
              </>
            ) : hasAnyDelivery ? (
              <>
                <div className="bg-slate-100 rounded-full p-4 mb-4">
                  <Package className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  {activeTab === "active" 
                    ? "Nenhuma entrega ativa" 
                    : activeTab === "history" 
                    ? "Nenhuma entrega no histórico"
                    : "Nenhuma entrega"}
                </h3>
                <p className="text-sm text-slate-500 max-w-xs mb-6">
                  {activeTab === "active" 
                    ? "Você não tem entregas em andamento no momento."
                    : activeTab === "history" 
                    ? "Você ainda não tem entregas concluídas ou canceladas."
                    : "Você não tem entregas nesta categoria."}
                </p>
                <Button
                  onClick={() => navigate({ to: "/app" })}
                  className="rounded-2xl px-6 h-11 font-semibold text-sm"
                >
                  Fazer nova entrega
                </Button>
              </>
            ) : (
              <>
                <div className="bg-primary/10 rounded-full p-5 mb-5">
                  <Package className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">
                  Nenhuma entrega ainda
                </h3>
                <p className="text-sm text-slate-500 max-w-xs mb-6">
                  Que tal enviar seu primeiro pacote? É rápido, fácil e chega com segurança.
                </p>
                <Button
                  onClick={() => navigate({ to: "/app" })}
                  className="rounded-2xl px-6 h-11 font-semibold text-sm"
                >
                  Fazer minha primeira entrega
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {solicitacoes.map((solicitacao) => {
              const statusColor = STATUS_COLORS[solicitacao.status];
              return (
                <button
                  key={solicitacao.id}
                  onClick={() =>
                    navigate({
                      to: "/app/entregas/$entregaId",
                      params: { entregaId: String(solicitacao.id) },
                    })
                  }
                  className="w-full bg-white rounded-2xl p-3 border border-black/5 hover:border-primary/20 hover:cursor-pointer transition-all group relative"
                >
                  <div className="absolute top-2 right-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColor.bg} ${statusColor.text}`}
                    >
                      <div
                        className={`h-1 w-1 rounded-full ${statusColor.dot}`}
                      />
                      {STATUS_LABELS[solicitacao.status]}
                    </span>
                  </div>
                  <div className="absolute bottom-2 right-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold`}
                    >
                      Acompanhar entrega
                      <ChevronRight className="h-3 w-3" />
                    </span>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center gap-1">
                      <div className="bg-primary/10 rounded-lg p-2">
                        <Package className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-[10px] text-slate-400 font-medium">
                        #{solicitacao.externalId}
                      </span>
                    </div>

                    <div className="flex-1 text-left min-w-0 pt-0.5">
                      <div className="flex items-start gap-1.5 mb-2">
                        <div className="flex flex-col items-center pt-0.5">
                          <div className="h-1 w-1 rounded-full bg-slate-400" />
                          <div className="w-px h-2.5 bg-slate-300" />
                          <MapPin className="h-2.5 w-2.5 text-slate-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-900 truncate">
                            {solicitacao.origemSnapshot?.nome || "Origem"}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {solicitacao.destinoSnapshot?.nome ||
                              "Destino"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 text-[11px] text-slate-400">
                        <Clock className="h-3 w-3" />
                        {new Date(solicitacao.createdAt).toLocaleDateString(
                          "pt-BR",
                          {
                            day: "2-digit",
                            month: "short",
                          }
                        )}{" "}
                        ・{" "}
                        {new Date(solicitacao.createdAt).toLocaleTimeString(
                          "pt-BR",
                          {
                            hour: "numeric",
                            minute: "2-digit",
                          }
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
